import { EnvVariables } from "../env-variables";
import { developmentConfig, type EnvironmentConfig } from "./environments/development";
import { productionConfig } from "./environments/production";
import { stagingConfig } from "./environments/staging";
import { testConfig } from "./environments/test";

/**
 * Get environment-specific configuration
 */
function getEnvironmentConfig(): EnvironmentConfig {
  switch (EnvVariables.env) {
    case "development":
      return developmentConfig;
    case "test":
      return testConfig;
    case "staging":
      return stagingConfig;
    case "production":
      return productionConfig;
    default:
      console.warn(`Unknown environment: ${EnvVariables.env}, falling back to development config`);
      return developmentConfig;
  }
}

/**
 * Application configuration combining environment variables and environment-specific settings
 */
export const AppConfig = {
  // Environment variables (validated)
  env: EnvVariables,

  // Environment-specific configuration
  ...getEnvironmentConfig(),

  // Common configuration that doesn't change between environments
  app: {
    name: "Echo Frontend",
    version: import.meta.env.VITE_RELEASE || "unknown",
    buildMode: import.meta.env.MODE,
  },

  // UI Configuration
  ui: {
    defaultLanguage: "en",
    supportedLanguages: ["en", "zh", "fr"],
    theme: {
      defaultTheme: "light",
      supportedThemes: ["light", "dark"],
    },
    pagination: {
      defaultPageSize: 20,
      pageSizeOptions: [10, 20, 50, 100],
    },
  },

  // API Configuration
  api: {
    retryAttempts: 3,
    retryDelay: 1000,
    defaultHeaders: {
      "Content-Type": "application/json",
    },
  },
} as const;

/**
 * Type for the complete application configuration
 */
export type AppConfig = typeof AppConfig;

/**
 * Helper functions for configuration access
 */
export const ConfigHelpers = {
  /**
   * Check if feature is enabled
   */
  isFeatureEnabled: (feature: keyof typeof AppConfig.features): boolean => {
    return AppConfig.features[feature];
  },

  /**
   * Get API timeout based on environment
   */
  getApiTimeout: (): number => {
    return AppConfig.apiTimeout;
  },

  /**
   * Check if debug mode is enabled
   */
  isDebugMode: (): boolean => {
    return AppConfig.enableDebugMode;
  },

  /**
   * Check if development tools should be enabled
   */
  shouldEnableDevTools: (): boolean => {
    return AppConfig.enableDevTools;
  },

  /**
   * Get cache timeout
   */
  getCacheTimeout: (): number => {
    return AppConfig.cacheTimeout;
  },
};

// Export environment-specific configurations for testing
export { developmentConfig, productionConfig, stagingConfig, testConfig };
export type { EnvironmentConfig };
